// ============================================================================
// PairsForm - Protected Route Component (v2 - RBAC ; v3 - niveau organisation)
// ============================================================================
// Lot 9.1 (2026-10-18, décision D2) : sous `/o/:slug/*`, l'URL porte l'org.
// Quand le segment diffère de l'org du jeton, la garde demande la bascule au
// serveur (`POST /auth/switch-org` par slug : membership réel ou immersion
// autorisée — la décision reste au switch, P8) et n'affiche la page qu'une
// fois le jeton aligné. Un lien `/o/client-x/…` reçu d'un collègue ouvre donc
// directement la bonne org ; une org inconnue ou interdite donne un écran
// explicite, jamais une page vide.

import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CircularProgress, Box, Button, Typography } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { ProtectedLayout } from "./layout";
import { messageApi } from "../lib/api";
import { segmentOrg, urlMonEspace } from "../lib/urls";
import type { UserRole } from "@cecodev/shared";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Rôles autorisés. Si omis, tout utilisateur authentifié a accès. */
  requiredRoles?: UserRole[];
  /** Si true, réservé aux orgs PLATEFORME : type CECODEV **ou** INTER
   *  (marketplace Cecodev_Inter), SUPER_ADMIN bypass. Ex-`cecodevOnly`,
   *  renommé (N198) : le nom mentait — il n'a jamais borné à la seule org
   *  cecodev depuis l'alignement sur `estAdminPlateforme` (serveur). */
  orgPlateformeOnly?: boolean;
}

function Chargement() {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export function ProtectedRoute({ children, requiredRoles, orgPlateformeOnly }: ProtectedRouteProps) {
  const { user, isLoading, basculerVersOrg } = useAuth();
  // `:slug` n'existe que sur les routes du niveau organisation (/o/:slug/…).
  const { slug } = useParams();
  const segmentJeton = user ? segmentOrg(user.organisationSlug, user.organisationId) : null;
  const doitBasculer = !!slug && !!user && slug !== segmentJeton;
  const [erreurBascule, setErreurBascule] = useState<string | null>(null);
  const basculeEnCours = useRef<string | null>(null);

  useEffect(() => {
    if (!doitBasculer || !slug || basculeEnCours.current === slug) return;
    basculeEnCours.current = slug;
    setErreurBascule(null);
    basculerVersOrg(slug)
      .catch((err: unknown) => setErreurBascule(messageApi(err, "Organisation inaccessible")))
      .finally(() => {
        basculeEnCours.current = null;
      });
  }, [doitBasculer, slug, basculerVersOrg]);

  if (isLoading) {
    return <Chargement />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (doitBasculer) {
    if (erreurBascule) {
      return (
        <ProtectedLayout>
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom>
              Organisation inaccessible
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {erreurBascule}
            </Typography>
            <Button component={Link} to={urlMonEspace()} variant="outlined" sx={{ textTransform: "none" }}>
              Retour à mon espace
            </Button>
          </Box>
        </ProtectedLayout>
      );
    }
    return <Chargement />;
  }

  // Vérification des rôles (ceux du jeton, donc de l'org de l'URL).
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user.roles ?? [];
    const hasRequiredRole = requiredRoles.some((r) => userRoles.includes(r));
    if (!hasRequiredRole) {
      return (
        <ProtectedLayout>
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom>
              Accès non autorisé
            </Typography>
            <Typography color="text.secondary">
              Vous n'avez pas les droits nécessaires pour accéder à cette page.
            </Typography>
          </Box>
        </ProtectedLayout>
      );
    }
  }

  // Vérification organisation CECODEV (SUPER_ADMIN bypass toujours).
  // Aligné sur la politique SERVEUR (estAdminPlateforme) : un PILOTE
  // d'org CECODEV **ou** INTER (marketplace Cecodev_Inter — y compris en
  // immersion, où le JWT est re-signé PILOTE/INTER) a accès à l'admin
  // plateforme. L'ancien gate (CECODEV seul) bloquait l'immersion dans
  // Cecodev_Inter alors que le serveur l'autorise.
  if (orgPlateformeOnly) {
    const userRolesForCecodev = user.roles ?? [];
    const isSuperAdmin = userRolesForCecodev.includes("SUPER_ADMIN");
    const orgAutorisee = user.organisationType === "CECODEV" || user.organisationType === "INTER";
    if (!isSuperAdmin && !orgAutorisee) {
      return (
        <ProtectedLayout>
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom>
              Accès réservé
            </Typography>
            <Typography color="text.secondary">
              Cette section est réservée à l'équipe CECODEV.
            </Typography>
          </Box>
        </ProtectedLayout>
      );
    }
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
}
